import React, { useState, useEffect } from "react";
import ParcoursServiceInstance from "service/parcours.service";
import HeaderSection from "components/headers-section";
import ActionBar from "components/action-bar";
import ParcoursTable from "./components/ParcoursTable";
import ParcoursFormModal from "./components/ParcoursFormModal";
import DeleteConfirmationModal from "components/delete-confirm-modal";

const ParcoursManager = () => {
  const [parcours, setParcours] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [currentParcours, setCurrentParcours] = useState({
    name_parcours: "",
    status_parcours: true,
  });
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    fetchParcours();
  }, []);

  const fetchParcours = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await ParcoursServiceInstance.getAll();
      setParcours(result || []);
    } catch (error) {
      setError(
        "Impossible de charger les parcours. Veuillez réessayer plus tard."
      );
      console.error("Error fetching parcours:", error);
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setCurrentParcours({
      name_parcours: "",
      status_parcours: true,
    });
    setIsEditing(false);
    setIsFormModalOpen(true);
  };

  const openEditModal = (parcours) => {
    setCurrentParcours({
      id_parcours: parcours.id_parcours,
      name_parcours: parcours.name_parcours,
      status_parcours: parcours.status_parcours,
    });
    setIsEditing(true);
    setIsFormModalOpen(true);
  };

  const openDeleteModal = (parcours) => {
    setCurrentParcours(parcours);
    setIsDeleteModalOpen(true);
  };

  const handleSubmitForm = async (formData) => {
    setLoading(true);
    try {
      if (isEditing) {
        await ParcoursServiceInstance.update(
          currentParcours.id_parcours,
          formData
        );
      } else {
        await ParcoursServiceInstance.create(formData);
      }

      fetchParcours();
      setIsFormModalOpen(false);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        errors: error.response?.data?.errors || {
          general: isEditing
            ? "Impossible de modifier le parcours. Veuillez réessayer."
            : "Impossible de créer le parcours. Veuillez réessayer.",
        },
      };
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      await ParcoursServiceInstance.delete(currentParcours.id_parcours);
      fetchParcours();
      setIsDeleteModalOpen(false);
    } catch (error) {
      setError("Impossible de supprimer le parcours. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  const filteredParcours = parcours.filter((p) =>
    p.name_parcours.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gray-50 px-5 py-8 md:px-8">
        <HeaderSection
          title="Gestion des Parcours"
          description="Créez et gérez les parcours de formation"
          accentColor="#57A863"
        />

        <ActionBar
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          onRefresh={fetchParcours}
          onCreateNew={openCreateModal}
          buttonLabel="Nouveau Parcours"
          loading={loading}
        />

        {error && (
          <div
            className="mb-6 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-600"
            role="alert"
          >
            {error}
          </div>
        )}

        <ParcoursTable
          parcours={filteredParcours}
          loading={loading}
          onEdit={openEditModal}
          onDelete={openDeleteModal}
        />

      <ParcoursFormModal
        isOpen={isFormModalOpen}
        onClose={() => setIsFormModalOpen(false)}
        onSubmit={handleSubmitForm}
        initialParcours={currentParcours}
        isEditing={isEditing}
        existingParcours={parcours}
        loading={loading}
      />

      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDelete}
        item={currentParcours}
        itemName={currentParcours.name_parcours}
        itemType="parcours"
        warning="La suppression de ce parcours peut affecter les étudiants qui y sont inscrits."
        loading={loading}
      />
    </div>
  );
};

export default ParcoursManager;
